
import { useQuery } from '@tanstack/react-query';
import { fetchSocialMediaSettings } from '@/services/appSettingsService';
import { debugLogger } from '@/utils/debug';

export interface SocialMediaLinks {
  facebook: string;
  instagram: string;
  linkedin: string;
  twitter: string;
}

export interface SocialMediaFlags {
  facebook: boolean;
  instagram: boolean;
  linkedin: boolean;
  twitter: boolean;
}

export const useSocialMediaSettings = () => {
  const getDefaultSettings = () => ({
    links: {
      facebook: '',
      instagram: '',
      linkedin: '',
      twitter: ''
    },
    flags: {
      facebook: false,
      instagram: false,
      linkedin: false,
      twitter: false
    }
  });

  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['socialMediaSettings'],
    queryFn: async () => {
      debugLogger.log('🔍 Fetching social media settings');
      
      try {
        const data = await fetchSocialMediaSettings();
        
        if (!data) {
          debugLogger.warn('No social media settings found');
          return getDefaultSettings();
        }

        debugLogger.log('✅ Social media settings fetched:', data);
        
        // Transform the flat array into structured objects
        const links: SocialMediaLinks = {
          facebook: '',
          instagram: '',
          linkedin: '',
          twitter: ''
        };
        
        const flags: SocialMediaFlags = {
          facebook: false,
          instagram: false,
          linkedin: false,
          twitter: false
        };
        
        data.forEach(setting => {
          switch (setting.key) {
            case 'SOCIAL_FACEBOOK_URL':
              links.facebook = setting.value || '';
              break;
            case 'SOCIAL_INSTAGRAM_URL':
              links.instagram = setting.value || '';
              break;
            case 'SOCIAL_LINKEDIN_URL':
              links.linkedin = setting.value || '';
              break;
            case 'SOCIAL_TWITTER_URL':
              links.twitter = setting.value || '';
              break;
            case 'ENABLE_SOCIAL_FACEBOOK':
              flags.facebook = setting.value === 'true';
              break;
            case 'ENABLE_SOCIAL_INSTAGRAM':
              flags.instagram = setting.value === 'true';
              break;
            case 'ENABLE_SOCIAL_LINKEDIN':
              flags.linkedin = setting.value === 'true';
              break;
            case 'ENABLE_SOCIAL_TWITTER':
              flags.twitter = setting.value === 'true';
              break;
          }
        });
        
        debugLogger.log('🔗 Processed social media links:', links);
        debugLogger.log('🎚️ Processed social media flags:', flags);
        
        return { links, flags };
      } catch (error) {
        debugLogger.error('❌ Error fetching social media settings:', error);
        return getDefaultSettings();
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    socialLinks: settings?.links || {
      facebook: 'https://facebook.com',
      instagram: 'https://instagram.com',
      linkedin: 'https://linkedin.com',
      twitter: 'https://twitter.com'
    },
    socialFlags: settings?.flags || {
      facebook: true,
      instagram: true,
      linkedin: true,
      twitter: true
    },
    isLoading,
    error
  };
};
